import { useEffect, useCallback, useRef } from 'react'
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom'
import { useAppContext, CompareSelection } from '../contexts/AppContext'
import { SensorSubType } from '../components/SensorTypeSelector'
import { isValidPortToken } from '../utils/portTokens'

/**
 * Hook to synchronize compare mode state with URL parameters on the /compare route.
 *
 * URL format:
 * /compare?devices=ABC123:A1.B2,DEF456:C2&type=Irrometer&subType=rainfallVolume&from=123&to=456
 *
 * - devices: Comma-separated deviceId:sensorPorts pairs
 * - type: Selected sensor type
 * - subType: Selected sub-type (for rainfall/flowMeter)
 * - from/to: Time range in seconds (same convention as useDeviceUrlSync)
 *
 * Port tokens may carry a sub-metric suffix in mixed mode ("A2~rate") — see
 * utils/portTokens. Bare tokens from older links keep working unchanged.
 */

const VALID_SUB_TYPES: string[] = [
  'rainfallVolume', 'rainfallRate', 'rainfallTotal',
  'flowVolume', 'flowRate', 'flowTotal',
]

function isValidSigfoxId(id: string): boolean {
  return /^[0-9A-Fa-f]{6}$/.test(id)
}

export function parseDevicesParam(devicesParam: string): {
  deviceIds: string[]
  sensors: CompareSelection[]
} {
  const deviceIds: string[] = []
  const sensors: CompareSelection[] = []
  const seen = new Set<string>()

  const parts = devicesParam.split(',')
  for (const part of parts) {
    const [deviceId, portsStr] = part.split(':')
    if (deviceId && isValidSigfoxId(deviceId) && !seen.has(deviceId)) {
      // Dedupe: a device may appear more than once in a shared/corrupted URL.
      // Keep only the first occurrence so we don't accumulate duplicate series.
      seen.add(deviceId)
      deviceIds.push(deviceId)
      if (portsStr) {
        // Drop anything that isn't a known port/sub-metric token so a
        // hand-edited or corrupted link can't inject bogus selections.
        const ports = portsStr.split('.').filter(isValidPortToken)
        if (ports.length > 0) {
          sensors.push({ deviceId, sensorPorts: ports })
        }
      }
    }
  }

  return { deviceIds, sensors }
}

export function serializeDevicesParam(
  deviceIds: string[],
  sensors: CompareSelection[]
): string {
  // Dedupe device ids before serializing so the URL never accumulates
  // duplicate entries even if the in-memory list somehow contains any.
  const uniqueDeviceIds = Array.from(new Set(deviceIds))
  return uniqueDeviceIds.map(deviceId => {
    const sensorSelection = sensors.find(s => s.deviceId === deviceId)
    if (sensorSelection && sensorSelection.sensorPorts.length > 0) {
      return `${deviceId}:${sensorSelection.sensorPorts.join('.')}`
    }
    return deviceId
  }).join(',')
}

export function useCompareUrlSync() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const isCompareRoute = location.pathname === '/compare'
  const hasRestoredFromUrl = useRef(false)

  const {
    compareMode,
    compareVariant,
    selectedDevicesForComparison,
    selectedSensorType,
    selectedSubType,
    selectedSensors,
    setCompareMode,
    setCompareVariant,
    setDevicesForComparison,
    setSelectedSensorType,
    setSelectedSubType,
    setSelectedSensors,
  } = useAppContext()

  // Restore compare state from URL on mount (only on /compare route)
  useEffect(() => {
    if (!isCompareRoute || hasRestoredFromUrl.current) return
    hasRestoredFromUrl.current = true

    const devicesParam = searchParams.get('devices')
    if (!devicesParam) {
      // On /compare with no devices param, just enable compare mode
      if (!compareMode) setCompareMode(true)
      return
    }

    const { deviceIds, sensors } = parseDevicesParam(devicesParam)
    if (deviceIds.length === 0) {
      if (!compareMode) setCompareMode(true)
      return
    }

    // Restore state
    setCompareMode(true)

    // Restore variant before sensors — switching variant clears selections,
    // so it must happen before setSelectedSensors below.
    if (searchParams.get('mode') === 'mixed') {
      setCompareVariant('mixed')
    }

    // Set the device list directly (idempotent). This hook mounts in several
    // components at once (page + sidebar/drawer toggles), so a toggle-append
    // loop would add each device once per instance — the source of the
    // duplicate rows. Setting replaces, so repeated restores are harmless.
    setDevicesForComparison(deviceIds)

    if (sensors.length > 0) {
      setSelectedSensors(sensors)
    }

    const typeParam = searchParams.get('type')
    if (typeParam) {
      setSelectedSensorType(typeParam)
    }

    const subTypeParam = searchParams.get('subType')
    if (subTypeParam && VALID_SUB_TYPES.includes(subTypeParam)) {
      setSelectedSubType(subTypeParam as SensorSubType)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Sync context state to URL when it changes (only on /compare route)
  useEffect(() => {
    if (!isCompareRoute || !compareMode) return

    const newParams = new URLSearchParams(searchParams)

    // Devices
    if (selectedDevicesForComparison.length > 0) {
      newParams.set('devices', serializeDevicesParam(selectedDevicesForComparison, selectedSensors))
    } else {
      newParams.delete('devices')
    }

    // Sensor type
    if (selectedSensorType) {
      newParams.set('type', selectedSensorType)
    } else {
      newParams.delete('type')
    }

    // Sub-type
    if (selectedSubType) {
      newParams.set('subType', selectedSubType)
    } else {
      newParams.delete('subType')
    }

    // Compare variant (only serialize the non-default 'mixed')
    if (compareVariant === 'mixed') {
      newParams.set('mode', 'mixed')
    } else {
      newParams.delete('mode')
    }

    // Only update if params actually changed
    const currentStr = searchParams.toString()
    const newStr = newParams.toString()
    if (currentStr !== newStr) {
      setSearchParams(newParams, { replace: true })
    }
  }, [
    isCompareRoute,
    compareMode,
    compareVariant,
    selectedDevicesForComparison,
    selectedSensorType,
    selectedSubType,
    selectedSensors,
    searchParams,
    setSearchParams,
  ])

  // Navigate to /compare route with current time range
  const navigateToCompare = useCallback((timeRange?: { from?: number; to?: number }) => {
    const params = new URLSearchParams()
    if (timeRange?.from) {
      params.set('from', Math.round(timeRange.from / 1000).toString())
    }
    if (timeRange?.to) {
      params.set('to', Math.round(timeRange.to / 1000).toString())
    }
    const queryString = params.toString()
    navigate(`/compare${queryString ? `?${queryString}` : ''}`)
  }, [navigate])

  // Navigate back to /charts
  const navigateFromCompare = useCallback((sigfoxId?: string) => {
    if (sigfoxId) {
      navigate(`/charts?sigfoxId=${sigfoxId}`)
    } else {
      navigate('/charts')
    }
  }, [navigate])

  return {
    isCompareRoute,
    navigateToCompare,
    navigateFromCompare,
  }
}
